import React, { useState } from "react"
import {
  RuleExecutionLog,
  MessageEntry,
  useEnableRuleTracking,
  useRuleExecutionLogs,
  useRuleTrackingIsEnabled,
} from "@/components/RuleExecutionLogs/rule-execution-logs-store"
import { Panel, useFloatingState } from "@/components/editor/panels/panel-registry"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui-primitives/resizable"
import { Badge } from "@/components/ui-primitives/badge"
import { Alert, AlertDescription } from "@/components/ui-primitives/alert"
import { Input } from "@/components/ui-primitives/small"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui-primitives/tabs"
import { TabsContent } from "@radix-ui/react-tabs"
import { mergeClasses } from "@/helpers/styling"
import { PanelChrome } from "@/components/editor/panels/panel-chrome"
import { Play, Square, CircleDot } from "lucide-react"

export function DebugRules() {
  const logs = useRuleExecutionLogs()
  const trackingEnabled = useRuleTrackingIsEnabled()
  const floatingState = useFloatingState()
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)

  if (!logs.length) {
    return (
      <div className="self-center text-gray-700 text-center m-4">
        {trackingEnabled
          ? "No rules have been executed since enabling tracking."
          : "Enable tracking (▶ in the header) to start recording rule executions."}
      </div>
    )
  }

  const selected = selectedIdx != null ? logs[selectedIdx] : null

  const logList = (
    <div className="flex flex-col p-1">
      {logs.map((item, idx) => (
        <button
          key={item.id}
          onClick={() => setSelectedIdx(idx)}
          className={mergeClasses(
            "w-full overflow-hidden flex flex-row text-left gap-1 text-xs border-y bg-opacity-50 hover:bg-opacity-75",
            {
              "bg-opacity-100 font-bold": idx === selectedIdx,
              "bg-green-200": item.success,
              "bg-red-200": !item.success,
            }
          )}
        >
          <Badge
            className={mergeClasses({
              "bg-green-600": item.success,
              "bg-red-600": !item.success,
            })}
          >
            {item.success ? "OK" : "FAIL"}
          </Badge>
          <span className="flex flex-grow items-center overflow-hidden text-ellipsis whitespace-nowrap">
            {item.tag}
          </span>
          <span className="flex items-center font-mono opacity-75 text-xs flex-shrink-0">[{item.timestamp}]</span>
        </button>
      ))}
    </div>
  )

  const detail = selected && (
    <div className="p-2">
      <RuleLogDetail log={selected} />
    </div>
  )

  if (floatingState === "docked") {
    return (
      <>
        {logList}
        {selectedIdx != null && (
          <Panel
            id="debug-rules-aux"
            role="auxiliary"
            title="Selected rule execution"
            onClose={() => setSelectedIdx(null)}
            defaultVisible
            defaultFloatingDimensions={{ width: 800, height: 600 }}
          >
            {detail}
          </Panel>
        )}
      </>
    )
  }

  return (
    <div className="min-h-0 h-full flex flex-col">
      <div className="min-h-0 h-full">
        <ResizablePanelGroup orientation="horizontal" className="max-h-full flex-grow h-full p-1">
          <ResizablePanel className="overflow-auto h-full flex flex-col p-1">{logList}</ResizablePanel>
          <ResizableHandle withHandle className="mx-1" />
          <ResizablePanel className="overflow-auto h-full flex flex-col p-2 text-sm">{detail}</ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  )
}

export function DebugRulesHeaderIcons() {
  const trackingEnabled = useRuleTrackingIsEnabled()
  const enableTracking = useEnableRuleTracking()
  return (
    <div className="my-[-2px] flex items-center gap-0.5">
      <PanelChrome.HeaderIcon
        title="Start rule tracking"
        icon={Play}
        iconSize={15}
        svgAttributes={!trackingEnabled ? { fill: "currentColor" } : {}}
        className={mergeClasses({ "text-green-700": !trackingEnabled })}
        onClick={() => enableTracking(true)}
        disabled={trackingEnabled}
      />
      <PanelChrome.HeaderIcon
        title="Stop rule tracking"
        icon={Square}
        iconSize={15}
        svgAttributes={trackingEnabled ? { fill: "currentColor" } : {}}
        className={mergeClasses({ "text-red-700": trackingEnabled })}
        onClick={() => enableTracking(false)}
        disabled={!trackingEnabled}
      />
    </div>
  )
}

export function DebugRulesBadge() {
  const trackingEnabled = useRuleTrackingIsEnabled()
  if (!trackingEnabled) return null
  return (
    <CircleDot
      size={9}
      className="position-absolute"
      style={{ top: "1px", right: "1px" }}
      stroke="green"
      fill="green"
    />
  )
}

function RuleLogDetail(props: { log: RuleExecutionLog }) {
  const { log } = props
  const [textFilter, setTextFilter] = useState("")

  return (
    <>
      <div className="mb-3 flex flex-row justify-between">
        <div>
          <span className="opacity-75 mr-2">tag</span>
          <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{log.tag}</code>
        </div>
        <div>
          <span className="opacity-75 mr-2">timestamp</span>
          <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{log.timestamp}</code>
        </div>
      </div>
      <div className="mb-3">
        <span className="opacity-75 mr-2">initial parameters</span>
        <pre className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{formatParams(log.params)}</pre>
      </div>
      <Tabs defaultValue="parsed" className="flex flex-col gap-2">
        <div className="mb-3 flex flex-row items-center">
          <span className="opacity-75 mr-2">log statements</span>
          <TabsList className="h-6">
            <TabsTrigger value="parsed" className="h-6">parsed</TabsTrigger>
            <TabsTrigger value="raw" className="h-6">raw</TabsTrigger>
          </TabsList>
        </div>
        {log.messages.some((m) => m.level === "WARN") && (
          <Alert className="bg-yellow-200 text-black">
            <AlertDescription>Rule engine reported one or more warnings.</AlertDescription>
          </Alert>
        )}
        {log.messages.some((m) => m.level === "ERROR") && (
          <Alert className="bg-red-200 text-black">
            <AlertDescription>Rule engine reported one or more errors.</AlertDescription>
          </Alert>
        )}
        <TabsContent value="parsed" className="p-1 rounded-lg border flex flex-column gap-2">
          <Input placeholder="filter" value={textFilter} onChange={(e) => setTextFilter(e.target.value)} />
          <div className="flex flex-col gap-1">
            {log.parsed
              .filter(
                (item) =>
                  !textFilter.length ||
                  item.tag.includes(textFilter) ||
                  item.uri.includes(textFilter) ||
                  item.params.includes(textFilter)
              )
              .map((item) => (
                <div
                  key={item.id}
                  className="relative rounded-md shadow-sm text-black flex flex-col text-left items-center font-mono text-sm border"
                >
                  {item.type === "before-rule" && (
                    <Badge className="text-black bg-yellow-100 hover:bg-yellow-300 w-full rounded-b-none shadow-none">BEFORE</Badge>
                  )}
                  {item.type === "after-rule" && (
                    <Badge className="text-black bg-orange-100 hover:bg-orange-300 w-full rounded-b-none shadow-none">AFTER</Badge>
                  )}
                  {item.type === "rule-errors" && (
                    <Badge className="text-white bg-red-500 hover:bg-red-600 w-full rounded-b-none shadow-none">ERRORS</Badge>
                  )}
                  <div className="grow flex flex-col px-2 py-1 w-full">
                    <div className="mb-3 flex flex-row items-center gap-2">
                      <span className="flex opacity-75">tag</span>
                      <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{item.tag}</code>
                      <span>
                        &lt;<code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{item.uri}</code>&gt;
                      </span>
                    </div>
                    {item.errors && item.errors.length > 0 && (
                      <div className="mb-3 px-2">
                        <span className="flex opacity-75">errors</span>
                        {item.errors.map((err, i) => (
                          <pre key={i} className="select-all text-red-900 bg-red-50 p-1 rounded-sm font-mono mb-1">{err}</pre>
                        ))}
                      </div>
                    )}
                    <div className="mb-3">
                      <span className="flex opacity-75">params</span>
                      <pre className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{item.params}</pre>
                    </div>
                  </div>
                  <span className="absolute right-0 top-0 opacity-75 text-xs select-all m-1">[{item.timestamp}]</span>
                </div>
              ))}
          </div>
        </TabsContent>
        <TabsContent value="raw" className="p-1 rounded-lg border flex flex-column gap-2">
          <Input placeholder="filter" value={textFilter} onChange={(e) => setTextFilter(e.target.value)} />
          <div>
            {log.messages
              .filter((item) => !textFilter.length || item.message.includes(textFilter))
              .map((item) => (
                <RawMessageEntry key={item.message + item.timestamp} entry={item} />
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </>
  )
}

function RawMessageEntry(props: { entry: MessageEntry }) {
  const hasMdc = props.entry.mdc && Object.keys(props.entry.mdc).length > 0
  return (
    <div
      className={mergeClasses("text-black flex flex-col text-left font-mono text-sm border-y", {
        "bg-slate-200": props.entry.level === "INFO",
        "bg-yellow-200": props.entry.level === "WARN",
        "bg-red-200": props.entry.level === "ERROR",
      })}
    >
      <div className="flex flex-row flex-grow gap-1 items-start">
        <div className="flex flex-col grow p-1 gap-1 rounded-sm overflow-x-auto">
          {hasMdc && (
            <details className="flex flex-col text-blue-950">
              <summary>Context</summary>
              <table className="ml-[15px]">
                <tbody>
                  {Object.entries(props.entry.mdc ?? {}).map(([key, value]) => (
                    <tr key={key}>
                      <th className="text-bold">{key}</th>
                      <td className="select-all">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
          {!!props.entry.stackTrace && (
            <details className="flex flex-col text-red-900">
              <summary>Stack trace</summary>
              <pre className="select-all text-red-900 ml-[15px]">{props.entry.stackTrace}</pre>
            </details>
          )}
          <pre className="grow select-all ml-[15px]">{props.entry.message.trim()}</pre>
          {!!props.entry.throwableMessage && (
            <pre className="grow select-all ml-[15px] text-red-900">{props.entry.throwableMessage.trim()}</pre>
          )}
        </div>
        <span className="opacity-75 text-xs select-none">[{props.entry.timestamp}]</span>
      </div>
    </div>
  )
}

function formatParams(params: string): string {
  try {
    if (!params) return ""
    return JSON.stringify(JSON.parse(params), null, 4)
  } catch {
    return params
  }
}
